import { stripShopifyInternalParams } from '@theme/utilities';

const MODAL_SELECTOR = '#quick-view-modal';
const BUTTON_SELECTOR = '.quick-view-button';
// Animation tuning
const TRANSITION_MS = 320;
const TRANSITION_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
const TRANSFORM_T = `transform ${TRANSITION_MS}ms ${TRANSITION_EASE}`;
// Pixel bleed to overlap images during slide and mask any seam
const BLEED_PX = 8;

/** @typedef {HTMLElement & { showAll?: () => void }} OverflowListElement */

/**
 * In Quick View we prefer showing all color swatches instead of collapsing them behind "More".
 * @param {ParentNode} root
 */
function showAllColorSiblingSwatches(root) {
  /** @type {NodeListOf<OverflowListElement>} */
  const overflowLists = /** @type {any} */ (
    root.querySelectorAll('fieldset[data-option-group="color_siblings"] overflow-list')
  );

  overflowLists.forEach((overflowList) => {
    try {
      if (typeof overflowList.showAll === 'function') overflowList.showAll();
    } catch (_) {}
  });
}

function toSlideOffsetPx(frameWidth) {
  return Math.max(1, frameWidth || 0);
}
// Drag completion rules
const COMPLETE_AT_ABS_PCT = 50; // if current image moved >= 50%
const FLICK_VELOCITY_PX_PER_MS = 0.9; // strong fling velocity (~900 px/s)

class QuickViewModal {
  constructor() {
    // Do not initialize on mobile viewports
    try {
      if (window.matchMedia && window.matchMedia('(max-width: 749px)').matches) return;
    } catch (_) {}

    /** @type {HTMLElement | null} */
    // Guard against duplicate modal instances in different layouts by preferring the last one in DOM
    const modals = /** @type {NodeListOf<HTMLElement>} */ (document.querySelectorAll(MODAL_SELECTOR));
    this.modal = modals[modals.length - 1] || null;
    if (!this.modal) return;

    this.dialog = /** @type {HTMLElement} */ (this.modal.querySelector('.quick-view-modal__dialog'));
    this.image = /** @type {HTMLImageElement} */ (this.modal.querySelector('[data-quick-view-image]'));
    this.mediaFrame = /** @type {HTMLElement} */ (this.modal.querySelector('.quick-view-modal__media-frame'));
    this.title = /** @type {HTMLElement} */ (this.modal.querySelector('[data-quick-view-title]'));
    this.vendor = /** @type {HTMLElement} */ (this.modal.querySelector('[data-quick-view-vendor]'));
    this.price = /** @type {HTMLElement} */ (this.modal.querySelector('[data-quick-view-price]'));
    this.description = /** @type {HTMLElement} */ (this.modal.querySelector('[data-quick-view-description]'));
    this.link = /** @type {HTMLAnchorElement} */ (this.modal.querySelector('[data-quick-view-link]'));
    this.dots = /** @type {HTMLElement} */ (this.modal.querySelector('[data-quick-view-dots]'));
    this.mediaContainer = /** @type {HTMLElement} */ (this.modal.querySelector('[data-quick-view-media]'));
    this.variantPickerMount = /** @type {HTMLElement} */ (this.modal.querySelector('[data-quick-view-variant-picker]'));
    this.productForm = /** @type {HTMLElement} */ (this.modal.querySelector('.quick-view-modal__form'));
    this.variantIdInput = /** @type {HTMLInputElement} */ (this.modal.querySelector('.quick-view-modal__form [ref="variantId"]'));
    this.addToCartBtn = /** @type {HTMLButtonElement} */ (this.modal.querySelector('.quick-view-modal__form [ref="addToCartButton"]'));

    this.prevButton = /** @type {HTMLButtonElement} */ (this.modal.querySelector('[data-quick-view-prev]'));
    this.nextButton = /** @type {HTMLButtonElement} */ (this.modal.querySelector('[data-quick-view-next]'));

    this.currentIndex = 0;
    this.images = [];
    this.pointerStartX = null;
    this.dragDeltaX = 0;
    this.dragActive = false;
    this.dragTargetX = 0;
    this.dragCurrentX = 0;
    this.dragRaf = null;
    this.dragHistory = [];
    /** @type {HTMLImageElement | null} */ this.prevImg = null;
    /** @type {HTMLImageElement | null} */ this.nextImg = null;

    if (this.image) {
      this.image.style.transform = 'translate3d(0px, 0, 0)';
      this.image.draggable = false;
      this.image.style.pointerEvents = 'none';
    }

    this.#bindEvents();
  }

  #handleColorSiblingNavigate = (event) => {
    if (!this.isOpen()) return;
    if (!(event.target instanceof Element)) return;

    /** @type {HTMLInputElement | null} */
    let input = null;
    if (event.target instanceof HTMLInputElement) {
      input = event.target;
    } else {
      const label = event.target.closest('label');
      input =
        label?.querySelector('input[data-option-group="color_siblings"][data-connected-product-url]') ||
        event.target.closest('input[data-option-group="color_siblings"][data-connected-product-url]');
    }
    if (!(input instanceof HTMLInputElement)) return;

    const url = input.dataset.connectedProductUrl;
    if (!url) return;
    const cleanUrl = stripShopifyInternalParams(url);

    // Avoid navigation when selecting the current product.
    try {
      const currentPath = new URL(this.link?.href || window.location.href, window.location.origin).pathname;
      const targetPath = new URL(url, window.location.origin).pathname;
      if (currentPath && targetPath && currentPath === targetPath) return;
    } catch (_) {}

    event.stopImmediatePropagation();
    event.preventDefault();

    this.hide();
    requestAnimationFrame(() => window.location.assign(cleanUrl || url));
  };

  #bindEvents() {
    this.modal.querySelectorAll('[data-quick-view-close]').forEach((closeButton) => {
      closeButton.addEventListener('click', () => this.hide());
    });

    this.prevButton?.addEventListener('click', () => this.showPrevious(true));
    this.nextButton?.addEventListener('click', () => this.showNext(true));

    // Listen for variant updates from the embedded picker and sync price/variantId/button
    this.modal.addEventListener('variant:update', (/** @type {CustomEvent} */ ev) => {
      const variant = ev?.detail?.resource;
      if (!variant) return;
      // Update price text
      const price = variant.price || 0;
      this.price.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD',
      }).format(price / 100);
      // Update hidden input/id and ATC state
      if (this.variantIdInput) this.variantIdInput.value = String(variant.id || '');
      if (this.addToCartBtn) this.addToCartBtn.disabled = variant.available === false;
    });

    document.addEventListener('keydown', (event) => {
      if (!this.isOpen()) return;

      if (event.key === 'Escape') this.hide();
      if (event.key === 'ArrowRight') this.showNext(true);
      if (event.key === 'ArrowLeft') this.showPrevious(true);
    });

    if (this.mediaContainer) {
      this.mediaContainer.addEventListener('pointerdown', this.#handlePointerDown);
      this.mediaContainer.addEventListener('pointermove', this.#handlePointerMove);
      this.mediaContainer.addEventListener('pointerup', this.#handlePointerUp);
      this.mediaContainer.addEventListener('pointercancel', this.#handlePointerUp);
      this.mediaContainer.addEventListener('pointerleave', this.#handlePointerUp);
    }

    document.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const button = target.closest(BUTTON_SELECTOR);
      if (!(button instanceof HTMLElement)) return;

      const handle = button.getAttribute('data-quick-view-handle');
      if (!handle) return;
      event.preventDefault();
      this.show(handle);
    });
  }

  #handlePointerDown = (event) => {
    // Ignore drag logic when clicking navigation buttons to avoid conflicts
    if (event.target instanceof Element && event.target.closest('[data-quick-view-prev], [data-quick-view-next]')) {
      return;
    }

    this.pointerStartX = event.clientX;
    this.pointerId = event.pointerId;
    this.dragDeltaX = 0;
    this.dragActive = true;
    this.dragCurrentX = 0;
    this.dragTargetX = 0;
    this.dragHistory = [{ x: event.clientX, t: performance.now() }];
    if (this.mediaContainer && typeof this.mediaContainer.setPointerCapture === 'function') {
      try { this.mediaContainer.setPointerCapture(event.pointerId); } catch (_) {}
    }
    this.#ensureNeighbors();
    if (this.mediaFrame) this.mediaFrame.style.cursor = 'grabbing';
  };

  #handlePointerMove = (event) => {
    if (this.pointerStartX === null) return;

    const delta = event.clientX - this.pointerStartX;
    this.dragDeltaX = delta;
    if (!this.mediaFrame || !this.image) return;
    event.preventDefault();
    const width = Math.max(1, this.mediaFrame.clientWidth);
    const clampedDelta = Math.max(-width, Math.min(width, delta));

    // Smooth to target translateX in px for seam-free alignment.
    this.dragTargetX = clampedDelta;
    // Track recent samples for velocity calc (last ~120ms)
    const now = performance.now();
    this.dragHistory.push({ x: event.clientX, t: now });
    const windowMs = 120;
    const cutoff = now - windowMs;
    while (this.dragHistory.length > 2 && this.dragHistory[0].t < cutoff) {
      this.dragHistory.shift();
    }
    if (!this.dragRaf) this.dragRaf = requestAnimationFrame(this.#applyDragSmooth);
  };

  #applyDragSmooth = () => {
    if (!this.mediaFrame || !this.image) { this.dragRaf = null; return; }
    // Exponential smoothing toward target
    const alpha = 0.35;
    this.dragCurrentX = this.dragCurrentX + (this.dragTargetX - this.dragCurrentX) * alpha;

    // Apply transforms without transitions
    this.image.style.transition = 'none';
    this.image.style.transform = `translate3d(${this.dragCurrentX}px, 0, 0)`;
    const slideOffset = toSlideOffsetPx(this.mediaFrame?.clientWidth || 0);
    if (this.nextImg) {
      this.nextImg.style.transition = 'none';
      this.nextImg.style.transform = `translate3d(${slideOffset + this.dragCurrentX}px, 0, 0)`;
    }
    if (this.prevImg) {
      this.prevImg.style.transition = 'none';
      this.prevImg.style.transform = `translate3d(${-slideOffset + this.dragCurrentX}px, 0, 0)`;
    }

    // Continue while dragging or until near target
    const stillDragging = this.dragActive;
    const notSettled = Math.abs(this.dragTargetX - this.dragCurrentX) > 0.5;
    if (stillDragging || notSettled) {
      this.dragRaf = requestAnimationFrame(this.#applyDragSmooth);
    } else {
      this.dragRaf = null;
    }
  };

  #handlePointerUp = (event) => {
    if (!this.mediaFrame || !this.image) return;
    if (this.pointerStartX === null) return; // ignore clicks that didn't start a drag
    const delta = this.dragDeltaX;
    const width = Math.max(1, this.mediaFrame.clientWidth);
    // Compute flick velocity over last ~120ms
    let velocity = 0; // px/ms
    if (this.dragHistory.length >= 2) {
      const first = this.dragHistory[0];
      const last = this.dragHistory[this.dragHistory.length - 1];
      const dt = Math.max(1, last.t - first.t);
      velocity = (last.x - first.x) / dt; // +right, -left
    }

    // Decide by position or velocity
    // Use raw pointer displacement to avoid smoothing lag at release
    const movedPct = Math.max(-100, Math.min(100, (delta / width) * 100));
    const movedFarEnough = Math.abs(movedPct) >= COMPLETE_AT_ABS_PCT;
    const strongFlick = Math.abs(velocity) >= FLICK_VELOCITY_PX_PER_MS;
    // Cancel any pending smoothing frames before animating
    if (this.dragRaf) { cancelAnimationFrame(this.dragRaf); this.dragRaf = null; }

    if (movedFarEnough) {
      const dir = movedPct < 0 ? 1 : -1; // moved left => next
      this.#animateDragComplete(dir);
    } else if (strongFlick) {
      const dir = velocity < 0 ? 1 : -1; // fling left => next
      this.#animateDragComplete(dir);
    } else {
      this.#animateDragCancel();
    }
    this.pointerStartX = null;
    this.dragDeltaX = 0;
    this.dragActive = false;
    this.dragTargetX = 0;
    // Let smoothing finish snapping back to 0
    if (this.mediaFrame) this.mediaFrame.style.cursor = 'grab';
    this.#releasePointer(event.pointerId);
  };

  async show(handle) {
    // Disable modal on mobile viewports
    try {
      if (window.matchMedia && window.matchMedia('(max-width: 749px)').matches) return;
    } catch (_) {}
    try {
      const product = await this.#fetchProduct(handle);
      this.#populate(product);
      this.modal.setAttribute('aria-hidden', 'false');
    } catch (error) {
      console.error('Quick view failed', error);
    }
  }

  hide() {
    this.modal.setAttribute('aria-hidden', 'true');
  }

  isOpen() {
    return this.modal.getAttribute('aria-hidden') === 'false';
  }

  showPrevious(animate = true) {
    if (!this.images.length) return;
    this.currentIndex = (this.currentIndex - 1 + this.images.length) % this.images.length;
    this.#updateImage(animate, -1);
  }

  showNext(animate = true) {
    if (!this.images.length) return;
    this.currentIndex = (this.currentIndex + 1) % this.images.length;
    this.#updateImage(animate, 1);
  }

  async #fetchProduct(handle) {
    const response = await fetch(`/products/${handle}.js`);
    if (!response.ok) throw new Error(`Failed to load product: ${response.status}`);
    return response.json();
  }

  #populate(product) {
    this.title.textContent = product.title || '';
    if (this.vendor) this.vendor.textContent = product.vendor || '';

    const price = product.price || 0;
    this.price.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: product.currency || window.Shopify?.currency?.active || 'USD',
    }).format(price / 100);

    const descriptionHtml = product.description || '';
    const temp = document.createElement('div');
    temp.innerHTML = descriptionHtml;
    const descriptionText = temp.textContent?.trim() || '';
    if (this.description) {
      this.description.textContent = descriptionText ? descriptionText.slice(0, 220) + (descriptionText.length > 220 ? '…' : '') : '';
    }
    this.link.href = product.url || `/products/${product.handle}`;

    this.images = Array.isArray(product.images) ? product.images : [];
    if (!this.images.length && product.featured_image) {
      this.images = [product.featured_image];
    }

    this.currentIndex = 0;
    this.#renderDots();
    this.#updateImage(false, 1);

    // Load server-rendered variant picker identical to product page
    this.#loadVariantPicker(product);
  }

  async #loadVariantPicker(product) {
    try {
      if (!this.variantPickerMount) return;
      const url = `/products/${product.handle}?section_id=section-rendering-quick-view`;
      const res = await fetch(url);
      if (!res.ok) return;
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const picker = doc.querySelector('variant-picker');
      if (!picker) return;
      // Adopt section-scoped styles, if any
      const styles = Array.from(doc.querySelectorAll('style'));
      styles.forEach((s) => {
        try { document.head.appendChild(s); } catch (_) {}
      });
      this.variantPickerMount.innerHTML = '';
      this.variantPickerMount.appendChild(picker);
      // In Quick View, clicking a color sibling swatch should navigate to that product and close the modal.
      picker.addEventListener('click', this.#handleColorSiblingNavigate, true);
      picker.addEventListener('change', this.#handleColorSiblingNavigate, true);
      // Ensure dataset updated
      picker.setAttribute('data-product-id', String(product.id || ''));
      picker.setAttribute('data-product-url', product.url || `/products/${product.handle}`);
      // Initialize inline size chart triggers inside the injected picker.
      try {
        picker.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
      } catch (_) {}

      // Ensure color sibling swatches aren't collapsed behind an overflow "More" button.
      showAllColorSiblingSwatches(picker);

      // Sync initial variant id for ATC
      const json = picker.querySelector('script[type="application/json"]')?.textContent;
      if (json) {
        try {
          const initial = JSON.parse(json);
          if (this.variantIdInput) this.variantIdInput.value = String(initial?.id || '');
          if (this.addToCartBtn) this.addToCartBtn.disabled = initial?.available === false;
        } catch (_) {}
      }
    } catch (e) {
      console.error('Quick view: failed to load variant picker', e);
    }
  }

  #renderDots() {
    if (!this.dots) return;
    this.dots.innerHTML = '';

    if (this.images.length <= 1) {
      this.dots.hidden = true;
      return;
    }

    this.dots.hidden = false;

    this.images.forEach((_, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'quick-view-modal__dot';
      dot.setAttribute('aria-label', `Go to image ${index + 1}`);
      dot.setAttribute('aria-current', index === this.currentIndex ? 'true' : 'false');
      dot.addEventListener('click', () => {
        if (index === this.currentIndex) return;
        const direction = index > this.currentIndex ? 1 : -1;
        this.currentIndex = index;
        this.#updateImage(true, direction);
      });
      this.dots.appendChild(dot);
    });
  }

  #updateImage(animate = false, direction = 1) {
    if (!this.image) return;

    const currentImage = this.images[this.currentIndex];
    if (!currentImage) return;

    const { src, width = 1200, height = 1200 } = this.#resolveImageData(currentImage);
    const sizedUrl = this.#buildSizedImage(src, 1200);

    if (!animate || !this.mediaFrame) {
      this.image.src = sizedUrl;
      this.image.alt = this.title.textContent || '';
      this.image.width = width;
      this.image.height = height;
      this.image.style.transform = 'translate3d(0px, 0, 0)';
      this.image.style.transition = '';
      this.image.draggable = false;
      this.image.style.pointerEvents = 'none';
      return;
    }

    const outgoing = this.image;
    const incoming = /** @type {HTMLImageElement} */ (outgoing.cloneNode(false));
    incoming.src = sizedUrl;
    incoming.alt = this.title.textContent || '';
    incoming.width = width;
    incoming.height = height;
    incoming.style.position = 'absolute';
    incoming.style.inset = '0';
    const slideOffset = toSlideOffsetPx(this.mediaFrame?.clientWidth || 0);
    incoming.style.transform = `translate3d(${direction > 0 ? slideOffset : -slideOffset}px, 0, 0)`;
    incoming.style.transition = TRANSFORM_T;
    incoming.style.pointerEvents = 'none';
    incoming.draggable = false;
    outgoing.style.transition = TRANSFORM_T;

    this.mediaFrame.appendChild(incoming);
    // Ensure layout flush before starting transition
    void incoming.offsetWidth;

    const startTransition = () => {
      incoming.style.transform = 'translate3d(0px, 0, 0)';
      outgoing.style.transform = `translate3d(${direction > 0 ? -slideOffset : slideOffset}px, 0, 0)`;
    };

    // Prefer decoding the incoming image before moving outgoing to avoid any visible gap
    try {
      if (typeof incoming.decode === 'function') {
        incoming.decode().catch(() => {}).finally(startTransition);
      } else {
        startTransition();
      }
    } catch (_) {
      startTransition();
    }

    const finalize = () => {
      outgoing.removeEventListener('transitionend', finalize);
      outgoing.remove();
      incoming.style.transition = '';
      this.image = incoming;
    };

    outgoing.addEventListener('transitionend', finalize, { once: true });

    if (this.dots && this.dots.children.length) {
      Array.from(this.dots.children).forEach((dot, index) => {
        dot.setAttribute('aria-current', index === this.currentIndex ? 'true' : 'false');
      });
    }
  }

  #buildSizedImage(src, size) {
    if (!src) return '';
    try {
      const url = new URL(src, window.location.origin);
      const segments = url.pathname.split('.');
      if (segments.length > 1) {
        segments[segments.length - 2] += `_${size}x`;
        url.pathname = segments.join('.');
      }
      return url.toString();
    } catch (error) {
      return src;
    }
  }

  #resolveImageData(image) {
    if (!image) return { src: '', width: 1200, height: 1200 };

    if (typeof image === 'string') {
      return { src: image, width: 1200, height: 1200 };
    }

    const src = image.src || image;
    const width = Number(image.width) || 1200;
    const height = Number(image.height) || width;

    return { src, width, height };
  }

  // Build neighbor images for drag interaction
  #ensureNeighbors() {
    if (!this.mediaFrame || !this.image || !this.images.length) return;
    // next
    if (!this.nextImg) {
      const nextIndex = (this.currentIndex + 1) % this.images.length;
      this.nextImg = this.#createImgForIndex(nextIndex);
      const slideOffset = toSlideOffsetPx(this.mediaFrame?.clientWidth || 0);
      this.nextImg.style.transform = `translate3d(${slideOffset}px, 0, 0)`;
      this.mediaFrame.appendChild(this.nextImg);
    }
    // prev
    if (!this.prevImg) {
      const prevIndex = (this.currentIndex - 1 + this.images.length) % this.images.length;
      this.prevImg = this.#createImgForIndex(prevIndex);
      const slideOffset = toSlideOffsetPx(this.mediaFrame?.clientWidth || 0);
      this.prevImg.style.transform = `translate3d(${-slideOffset}px, 0, 0)`;
      this.mediaFrame.appendChild(this.prevImg);
    }
  }

  #createImgForIndex(index) {
    const data = this.#resolveImageData(this.images[index]);
    const img = /** @type {HTMLImageElement} */ (this.image.cloneNode(false));
    img.removeAttribute('loading');
    img.src = this.#buildSizedImage(data.src, 1200);
    img.alt = this.title.textContent || '';
    img.width = data.width || 1200;
    img.height = data.height || img.width;
    img.style.position = 'absolute';
    img.style.inset = '0';
    img.style.pointerEvents = 'none';
    img.style.transition = 'none';
    return img;
  }

  #animateDragCancel() {
    const t = TRANSFORM_T;
    const slideOffset = toSlideOffsetPx(this.mediaFrame?.clientWidth || 0);
    if (this.image) { this.image.style.transition = t; this.image.style.transform = 'translate3d(0px, 0, 0)'; }
    if (this.nextImg) { this.nextImg.style.transition = t; this.nextImg.style.transform = `translate3d(${slideOffset}px, 0, 0)`; }
    if (this.prevImg) { this.prevImg.style.transition = t; this.prevImg.style.transform = `translate3d(${-slideOffset}px, 0, 0)`; }
    const cleanup = () => this.#cleanupNeighbors();
    this.image?.addEventListener('transitionend', cleanup, { once: true });
  }

  #animateDragComplete(dir) {
    // dir: 1 -> next, -1 -> previous
    const target = dir > 0 ? this.nextImg : this.prevImg;
    if (!target || !this.image) { this.#animateDragCancel(); return; }
    const t = TRANSFORM_T;
    const slideOffset = toSlideOffsetPx(this.mediaFrame?.clientWidth || 0);
    this.image.style.transition = t;
    target.style.transition = t;
    this.image.style.transform = `translate3d(${dir > 0 ? -slideOffset : slideOffset}px, 0, 0)`;
    target.style.transform = 'translate3d(0px, 0, 0)';
    const finalize = () => {
      this.image?.removeEventListener('transitionend', finalize);
      this.image?.remove();
      this.image = target;
      this.image.style.transition = '';
      this.image.style.transform = 'translate3d(0px, 0, 0)';
      // Update index and cleanup neighbors
      this.currentIndex = (this.currentIndex + (dir > 0 ? 1 : -1) + this.images.length) % this.images.length;
      this.#cleanupNeighbors();
    };
    this.image.addEventListener('transitionend', finalize, { once: true });
  }

  #cleanupNeighbors() {
    if (this.nextImg && this.nextImg !== this.image) { try { this.nextImg.remove(); } catch(_) {} }
    if (this.prevImg && this.prevImg !== this.image) { try { this.prevImg.remove(); } catch(_) {} }
    this.nextImg = null;
    this.prevImg = null;
  }

  #releasePointer(pointerId) {
    if (!this.mediaContainer || typeof this.mediaContainer.releasePointerCapture !== 'function') return;
    if (pointerId == null) return;
    try {
      this.mediaContainer.releasePointerCapture(pointerId);
    } catch (_) {}
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new QuickViewModal();
});
